import React, { useState } from "react";
import { FiX } from "react-icons/fi";
import type { SurveyQuestionSuggestion } from "../../lib/api";
import { uniqueOptionId } from "./builderUtils";

type Props = {
  suggestions: SurveyQuestionSuggestion[];
  disabled?: boolean;
  onChange: (next: SurveyQuestionSuggestion[]) => void;
};

/**
 * The tap-to-insert answer chips under a text question. In the app these are appended to
 * whatever the user has already typed, so they read as starting points rather than as a
 * fixed option list.
 */
const SuggestionsEditor: React.FC<Props> = ({
  suggestions,
  disabled,
  onChange,
}) => {
  const [draft, setDraft] = useState("");

  const add = () => {
    const label = draft.trim();
    if (!label) return;
    if (
      suggestions.some((s) => s.label.toLowerCase() === label.toLowerCase())
    ) {
      setDraft("");
      return;
    }
    // Answers record the id, so give each chip one up front rather than letting the server
    // mint it — that way a chip keeps its identity if the label is edited later.
    const id = uniqueOptionId(label, new Set(suggestions.map((s) => s.id)));
    onChange([...suggestions, { id, label }]);
    setDraft("");
  };

  return (
    <div className="space-y-2.5">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#5C6B76]">
          Suggested answers
        </p>
        <p className="mt-1 text-[11px] leading-relaxed text-[#869AA9]">
          Optional. Shown as tappable chips so users can answer in one tap
          instead of typing.
        </p>
      </div>

      {suggestions.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {suggestions.map((s) => (
            <li
              key={s.id}
              className="inline-flex items-center gap-1.5 rounded bg-secondary_button/15 py-1 pl-2.5 pr-1.5 text-[12px] font-semibold text-secondary_button"
            >
              {s.label}
              <button
                type="button"
                disabled={disabled}
                onClick={() =>
                  onChange(suggestions.filter((x) => x.id !== s.id))
                }
                aria-label={`Remove “${s.label}”`}
                className="rounded p-0.5 transition hover:bg-secondary_button/20 disabled:opacity-40"
              >
                <FiX className="text-xs" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex gap-2">
        <input
          type="text"
          value={draft}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder="Type an answer and press Enter"
          className="h-10 min-w-0 flex-1 rounded-lg border border-[#2C363E] bg-[#13181B] px-3 text-[13px] text-[#E7EBEE] outline-none transition placeholder:text-[#5C6B76] focus:border-secondary_button disabled:opacity-60"
        />
        <button
          type="button"
          disabled={disabled || !draft.trim()}
          onClick={add}
          className="shrink-0 rounded-lg border border-[#3A4852] bg-[#1C2227] px-3 text-[12px] font-semibold text-[#C6D0D7] transition hover:text-white disabled:opacity-40"
        >
          Add
        </button>
      </div>
    </div>
  );
};

export default SuggestionsEditor;
